import React, { useState, useEffect } from 'react';
import {
  BookOpen,
  X,
  Info,
  CalendarDays,
  CalendarClock,
  Navigation,
  PencilLine,
  Cpu,
} from 'lucide-react';
import '../CSS/GuideModal.css';

const SchedulingGuideModal = () => {
  const [isOpen, setIsOpen] = useState(false);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    if (isOpen) document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) setIsOpen(false);
  };

  return (
    <>
      {/* ── Floating Action Button ── */}
      <div className="gml-fab-wrapper">
        <span className="gml-fab-tooltip">Need help?</span>
        <button
          className="gml-fab"
          onClick={() => setIsOpen(true)}
          aria-label="Open Scheduling Guide"
          title="Scheduling Guide"
        >
          <BookOpen className="gml-fab-icon" />
        </button>
      </div>

      {/* ── Modal ── */}
      {isOpen && (
        <div
          className="gml-backdrop"
          onClick={handleBackdropClick}
          role="dialog"
          aria-modal="true"
          aria-labelledby="gml-modal-title"
        >
          <div className="gml-modal">

            {/* Header */}
            <div className="gml-modal-header">
              <div className="gml-modal-title-wrap">
                <BookOpen className="gml-modal-header-icon" />
                <h2 className="gml-modal-title" id="gml-modal-title">
                  Scheduling Module Guide
                </h2>
              </div>
              <button
                className="gml-modal-close"
                onClick={() => setIsOpen(false)}
                aria-label="Close guide"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="gml-modal-body">

              {/* Welcome Banner */}
              <div className="gml-welcome-banner">
                <Info className="gml-welcome-icon" />
                <div>
                  <p className="gml-welcome-heading">
                    Welcome to the Scheduling Module!
                  </p>
                  <p className="gml-welcome-text">
                    This guide will help you effectively manage all events and
                    schedules in your institution. Use it anytime you need a
                    quick reference.
                  </p>
                </div>
              </div>

              {/* ── Key Features Overview ── */}
              <h3 className="gml-section-heading">Key Features Overview</h3>
              <div className="gml-cards-grid">

                {/* Calendar View */}
                <div className="gml-feature-card gml-feature-card--blue">
                  <div className="gml-card-head gml-card-head--blue">
                    <CalendarDays className="gml-card-head-icon" />
                    <h4 className="gml-card-head-title">Calendar View</h4>
                  </div>
                  <div className="gml-card-body">
                    <ul className="gml-card-list">
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--blue">1</span>
                        Interactive monthly, weekly, and daily views
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--blue">2</span>
                        Drag-and-drop time slot selection
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--blue">3</span>
                        Color-coded events for easy identification
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Event Management */}
                <div className="gml-feature-card gml-feature-card--green">
                  <div className="gml-card-head gml-card-head--green">
                    <CalendarClock className="gml-card-head-icon" />
                    <h4 className="gml-card-head-title">Event Management</h4>
                  </div>
                  <div className="gml-card-body">
                    <ul className="gml-card-list">
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--green">1</span>
                        Create events directly on the calendar
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--green">2</span>
                        Add detailed events using the form
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--green">3</span>
                        Delete events with one click
                      </li>
                    </ul>
                  </div>
                </div>

              </div>

              {/* ── Detailed Feature Guide ── */}
              <h3 className="gml-section-heading">Detailed Feature Guide</h3>
              <div className="gml-detail-section">

                {/* Using the Calendar */}
                <div className="gml-detail-card gml-detail-card--cyan">
                  <div className="gml-detail-head gml-detail-head--cyan">
                    <Navigation className="gml-detail-head-icon" />
                    <h5 className="gml-detail-title">Using the Calendar</h5>
                  </div>
                  <div className="gml-detail-body">
                    <p className="gml-detail-sub gml-detail-sub--cyan">Navigation</p>
                    <ul className="gml-detail-list">
                      <li>Use the arrow buttons to move between months</li>
                      <li>Click "Today" to return to the current date</li>
                      <li>Switch between month, week, and day views using the toolbar</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--cyan">Creating Events</p>
                    <ul className="gml-detail-list">
                      <li><strong>Quick Add:</strong> Click and drag on the calendar to select a time slot, then enter a title</li>
                      <li><strong>Detailed Add:</strong> Use the form to specify exact start and end times</li>
                      <li>Events are automatically saved and persist between sessions</li>
                    </ul>
                  </div>
                </div>

                {/* Event Management */}
                <div className="gml-detail-card gml-detail-card--yellow">
                  <div className="gml-detail-head gml-detail-head--yellow">
                    <PencilLine className="gml-detail-head-icon" />
                    <h5 className="gml-detail-title">Event Management</h5>
                  </div>
                  <div className="gml-detail-body">
                    <p className="gml-detail-sub gml-detail-sub--yellow">Adding Events</p>
                    <ul className="gml-detail-list">
                      <li><strong>Title:</strong> Brief description of the event (required)</li>
                      <li><strong>Start Date / Time:</strong> Must be before end time (required)</li>
                      <li><strong>End Date / Time:</strong> Must be after start time (required)</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--yellow">Editing Events</p>
                    <ul className="gml-detail-list">
                      <li>Currently, events must be deleted and recreated to make changes</li>
                      <li>Future updates may include direct in-place event editing</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--yellow">Deleting Events</p>
                    <ul className="gml-detail-list">
                      <li>Click the "Delete" button on any event to remove it</li>
                      <li>Deletions are immediate and permanent</li>
                    </ul>
                  </div>
                </div>

                {/* Technical Features */}
                <div className="gml-detail-card gml-detail-card--green">
                  <div className="gml-detail-head gml-detail-head--green">
                    <Cpu className="gml-detail-head-icon" />
                    <h5 className="gml-detail-title">Technical Features</h5>
                  </div>
                  <div className="gml-detail-body">
                    <p className="gml-detail-sub gml-detail-sub--green">Data Persistence</p>
                    <ul className="gml-detail-list">
                      <li>Events are automatically saved to your browser's local storage</li>
                      <li>Data remains available between sessions</li>
                      <li>Works offline after the initial page load</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--green">Responsive Design</p>
                    <ul className="gml-detail-list">
                      <li>Calendar adapts to different screen sizes</li>
                      <li>Mobile-friendly views available</li>
                      <li>Optimized for both desktop and tablet use</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--green">Validation</p>
                    <ul className="gml-detail-list">
                      <li>
                        <span className="gml-inline-badge gml-inline-badge--success">Required</span>
                        — Prevents creating events with missing information
                      </li>
                      <li>
                        <span className="gml-inline-badge gml-inline-badge--muted">Time check</span>
                        — Ensures end time is always after start time
                      </li>
                      <li>Clear error messages guide corrections</li>
                    </ul>
                  </div>
                </div>

              </div>

              {/* ── Best Practices ── */}
              <div className="gml-best-practices">
                <p className="gml-best-practices-heading">Best Practices</p>
                <ul className="gml-best-practices-list">
                  {[
                    'Use clear, descriptive event titles for easy identification.',
                    'Set realistic time spans to avoid scheduling conflicts.',
                    'Regularly review and clean up old or outdated events.',
                    'Use the form for precise time scheduling instead of quick-add.',
                    'Take advantage of different calendar views for better planning.',
                  'Access this guide anytime via the floating button on the bottom-right of the screen.',
                  ].map((tip, i) => (
                    <li key={i}>
                      <span className="gml-bp-dot" aria-hidden="true" />
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>

            </div>

            {/* Footer */}
            <div className="gml-modal-footer">
              <button className="gml-close-btn" onClick={() => setIsOpen(false)}>
                Got it
              </button>
            </div>

          </div>
        </div>
      )}
    </>
  );
};

export default SchedulingGuideModal;